import { CSSProperties, useState } from 'react';
import CardSymphony from 'elements/CardSymphony';
import { httpsLink, makeKey, styx } from 'modules/Utils';
import WebsiteBuilderHeader from '../WebsiteBuilderHeader';
import useWebsiteBuilder, { getStreamingLinksFromRecord, STREAMING_PLATFORMS_WEBSITE_CONTENT, StreamingPlatformsStyle, WebsiteBuilder_streamingPlatformsExists } from 'Hooks/useWebsiteBuilder';
import ButtonSymphony from 'elements/ButtonSymphony';
import PlatformLink from 'elements/PlatformLink';
import ModalSymphony from 'elements/ModalSymphony';
import Icon from 'elements/Icon';
import clsx from 'clsx';
import Colors from 'modules/Colors';
import { DraggableList } from 'components/shareable/DraggableList';
import OptionsSymphony, { OptionItem } from 'elements/OptionsSymphony';
import { StackedWebsiteLayoutIcon, TraditionalWebsiteLayoutIcon } from 'components/svg-icons/website/themes/icons';
import { BoxesWebsiteLayoutIcon } from 'components/svg-icons/website/themes/icons';
import RecordSearchView from 'elements/RecordSearchView';
import useScreen from 'Hooks/useScreen';
import { RecordDetails } from 'modules/Types';
import { STREAMING_PLATFORMS_INFO } from 'modules/Const';
import WebsiteBuilderFooter from '../WebsiteBuilderFooter';
import WebsiteBuilderEmptyState from '../WebsiteBuilderEmptyState';

const DISABLED_OPACITY = 0.25;

interface StreamingPlatformsContentProps {
    className: string;
    contentIndex: number;
    tabSelectorComponent: React.ReactNode;
    id: string;
}

const StreamingPlatformsContent = ({
    className,
    contentIndex,
    tabSelectorComponent,
    id,
}: StreamingPlatformsContentProps) => {
    const {
        cssCalc,
        mobileView,
    } = useScreen()

    const {
        websiteContent,
        setWebsiteContent,
    } = useWebsiteBuilder() as WebsiteBuilder_streamingPlatformsExists;

    const streamingPlatformsContent = websiteContent[contentIndex].streamingPlatformsContent;
    const platforms = streamingPlatformsContent.platforms;
    const streamingPlatformsContentStyle = streamingPlatformsContent.style;
    const requiresPaidPlan = streamingPlatformsContentStyle === StreamingPlatformsStyle.ICONS || streamingPlatformsContentStyle === StreamingPlatformsStyle.STACKED_BOXES;

    const [showAddPlatformModal, setShowAddPlatformModal] = useState<boolean>(false);
    const [showRecordSelectorModal, setShowRecordSelectorModal] = useState<boolean>(false);
    const [showStreamingPlatformsModal, setShowStreamingPlatformsModal] = useState<boolean>(false);

    const STREAMING_PLATFORM_STYLE_OPTIONS: OptionItem[] = [
        {
            id: StreamingPlatformsStyle.BOXES,
            label: 'Boxes',
            childrenLeft: (
                <BoxesWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={streamingPlatformsContent.style === StreamingPlatformsStyle.BOXES}
                />
            )
        },
        {
            id: StreamingPlatformsStyle.TRADITIONAL,
            label: 'Traditional',
            childrenLeft: (
                <TraditionalWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={streamingPlatformsContent.style === StreamingPlatformsStyle.TRADITIONAL}
                />
            )
        },
        {
            id: StreamingPlatformsStyle.STACKED,
            label: 'Stacked',
            childrenLeft: (
                <StackedWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={streamingPlatformsContent.style === StreamingPlatformsStyle.STACKED}
                />
            )
        },
        {
            id: StreamingPlatformsStyle.STACKED_BOXES,
            label: 'Stacked Boxes',
            iconRight: 'gem',
            childrenLeft: (
                <StackedWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={streamingPlatformsContent.style === StreamingPlatformsStyle.STACKED_BOXES}
                />
            )
        },
        {
            id: StreamingPlatformsStyle.ICONS,
            label: 'Icons',
            iconRight: 'gem',
            childrenLeft: (
                <TraditionalWebsiteLayoutIcon
                    width={30}
                    height={30}
                    selected={streamingPlatformsContent.style === StreamingPlatformsStyle.ICONS}
                />
            )
        },
    ]

    const isSecondaryColor = () => {
        switch (streamingPlatformsContentStyle) {
            case StreamingPlatformsStyle.BOXES:
            case StreamingPlatformsStyle.STACKED_BOXES:
            case StreamingPlatformsStyle.TRADITIONAL:
                return true;
            default:
                return false;
        }
    }

    const isTextSecondaryColor = () => {
        switch (streamingPlatformsContentStyle) {
            case StreamingPlatformsStyle.TRADITIONAL:
                return true;
            default:
                return false;
        }
    }

    const handleSelectRecord = (recordDetails: RecordDetails) => {
        const platformsLocal = getStreamingLinksFromRecord(recordDetails);

        const websiteContentLocal = [...websiteContent];
        websiteContentLocal[contentIndex].streamingPlatformsContent.platforms = platformsLocal;
        setWebsiteContent(websiteContentLocal);

        setShowRecordSelectorModal(false);
    }

    const renderStreamingPlatforms = () => {
        return (
            <div
                className="streamingPlatformsContentPlatformsContainer"
                style={sty.streamingPlatformsContentPlatformsContainer}
            >
                {platforms.map((platform, index) => {
                    const platformName = platform.name;
                    const platformInfo = STREAMING_PLATFORMS_INFO[platformName];
                    if (!platformInfo) {
                        return null
                    }

                    const {
                        iconColor,
                        backgroundColor,
                        title,
                    } = platformInfo;

                    return (
                        <div
                            key={makeKey(platform.name, index)}
                            className="streamingPlatformsContentPlatform"
                            style={styx(
                                sty.streamingPlatformsContentPlatform,
                                {
                                    backgroundColor,
                                    opacity: platform.isEnabled ? 1 : DISABLED_OPACITY,
                                }
                            )}
                            onClick={() => setShowStreamingPlatformsModal(true)}
                        >
                            <Icon
                                className="streamingPlatformsContentPlatformIcon"
                                icon={iconColor || ''}
                                size={28}
                            />
                            <div>{title}</div>
                            <Icon
                                className="streamingPlatformsContentPlatformIcon"
                                icon="edit"
                                size={22}
                                color={Colors.greyDark}
                            />
                        </div>
                    )
                })}
            </div>
        )
    }
    const renderRightSideContent = () => {
        return (
            <>
                <ButtonSymphony
                    className="streamingPlatformsContentAddPlatformButton"
                    iconLeft={mobileView ? 'music' : 'addCircle'}
                    onClick={() => setShowRecordSelectorModal(true)}
                    variant="text"
                >
                    {mobileView ? '' : 'Import from release'}
                </ButtonSymphony>
                {mobileView ?
                    <Icon
                        className="streamingPlatformsContentAddPlatformButtonIcon"
                        icon="addCircle"
                        size={24}
                        onClick={() => setShowAddPlatformModal(true)}
                    />
                    :
                    <ButtonSymphony
                        className="streamingPlatformsContentAddPlatformButton"
                        iconLeft="addCircle"
                        onClick={() => setShowAddPlatformModal(true)}
                        variant="text"
                    >
                        Edit Platform
                    </ButtonSymphony>
                }
            </>
        )
    }

    // NOTE: render()
    return (
        <>
            <CardSymphony
                className={clsx("streamingPlatformsContentComponent", className)}
                style={styx(sty.streamingPlatformsContentComponent)}
                size="small"
                id={id}
            >
                <WebsiteBuilderHeader
                    contentIndex={contentIndex}
                    rightSideContent={
                        <>
                            {tabSelectorComponent}
                            {renderRightSideContent()}
                        </>
                    }
                />

                {!platforms.length &&
                    <WebsiteBuilderEmptyState
                        className="streamingPlatformsContentEmptyState"
                        title="No streaming platforms added"
                        description="Add a streaming platform to your website to get started."
                        isHorizontal
                        buttonText="Add Platform"
                        onClickButton={() => setShowAddPlatformModal(true)}
                    />
                }

                {renderStreamingPlatforms()}

                <WebsiteBuilderFooter
                    className="streamingPlatformsContentFooter"
                    contentIndex={contentIndex}
                    isBackgroundColor
                    isSecondaryColor={isSecondaryColor()}
                    isTextColor
                    isTextSecondaryColor={isTextSecondaryColor()}
                    requiresPaidPlan={requiresPaidPlan}
                    titleRequired="This Streaming Platforms style requires a paid plan to publish your website."
                    styleOptions={
                        <OptionsSymphony
                            className="streamingPlatformsContentStyleOptions"
                            style={{ flexWrap: 'wrap' }}
                            options={STREAMING_PLATFORM_STYLE_OPTIONS}
                            value={streamingPlatformsContentStyle || ''}
                            handleOptionSelected={(option) => {
                                const websiteContentLocal = [...websiteContent];
                                websiteContentLocal[contentIndex].streamingPlatformsContent.style = option as StreamingPlatformsStyle;
                                setWebsiteContent(websiteContentLocal);
                            }}
                            size="small"
                            isRadio={false}
                            childrenLeftOffset={-4}
                        />
                    }
                />
            </CardSymphony>

            {/* MODALS */}
            <ModalSymphony
                className="streamingPlatformsContentAddPlatformModal"
                isOpen={showAddPlatformModal}
                onClose={() => setShowAddPlatformModal(false)}
                size="small"
                title="Add Streaming Platform"
                style={{ gap: 8 }}
            >
                {STREAMING_PLATFORMS_WEBSITE_CONTENT.map((platform, index) => {
                    const isPlatformAdded = platforms.some(p => p.name === platform.name);

                    const {
                        iconColor,
                        backgroundColor,
                        title,
                    } = STREAMING_PLATFORMS_INFO[platform.name] || {};

                    return (
                        <div
                            key={makeKey(platform.name, index)}
                            className={clsx(
                                'streamingPlatformsContentAddPlatformModalPlatform',
                                { streamingPlatformsContentAddPlatformModalPlatformDisabled: isPlatformAdded }
                            )}
                            style={styx(
                                sty.streamingPlatformsContentAddPlatformModalPlatform,
                                isPlatformAdded && sty.streamingPlatformsContentAddPlatformModalPlatformDisabled,
                                { backgroundColor }
                            )}
                            onClick={() => {
                                if (isPlatformAdded) {
                                    return;
                                }

                                setShowAddPlatformModal(false);
                                const websiteContentLocal = [...websiteContent];
                                websiteContentLocal[contentIndex].streamingPlatformsContent.platforms.push({
                                    ...platform,
                                    title,
                                    icon: iconColor || '',
                                    description: title,
                                    isEnabled: true,
                                });
                                setWebsiteContent(websiteContentLocal);
                            }}
                        >
                            <Icon
                                className="streamingPlatformsContentAddPlatformModalIcon"
                                icon={iconColor || ''}
                                size={28}
                                style={isPlatformAdded ? { opacity: 0.5 } : undefined}
                            />
                            {title}
                            {isPlatformAdded &&
                                <span style={sty.alreadyAddedText}>
                                    Already added
                                </span>
                            }
                        </div>
                    )
                })}
            </ModalSymphony>

            <ModalSymphony
                className="musicContentRecordSelectorModal"
                isOpen={showRecordSelectorModal}
                onClose={() => setShowRecordSelectorModal(false)}
                title="Search Music"
                subtitle="Search for music to add to your website."
            >
                <RecordSearchView
                    handleSelectRecord={handleSelectRecord}
                    options={['single', 'project', 'playlist']}
                    letUserSearchSpotify
                    letUserRefreshReleases
                    maxHeight={cssCalc(64 + 340)}
                />
            </ModalSymphony>

            <ModalSymphony
                className="streamingPlatformsContentCustomizeStyleModal"
                isOpen={showStreamingPlatformsModal}
                onClose={() => setShowStreamingPlatformsModal(false)}
                title="Streaming Platforms"
                subtitle="Edit your streaming platforms"
                buttonRightText="Done"
                onClickButtonRight={() => {
                    setShowStreamingPlatformsModal(false);
                }}
                onClickButtonLeft={() => {
                    setShowStreamingPlatformsModal(false);
                    setShowAddPlatformModal(true);
                }}
                buttonLeftText="Add Platform"
                buttonLeftVariant="outlined"
            >
                <DraggableList
                    id={`streamingPlatformsContentDraggableList-${streamingPlatformsContent.title}`}
                    list={platforms}
                    keyOfItem="name"
                    isDragHandle
                    updateList={(newList) => {
                        const websiteContentLocal = [...websiteContent];
                        websiteContentLocal[contentIndex].streamingPlatformsContent.platforms = newList;
                        setWebsiteContent(websiteContentLocal);
                    }}
                    renderItem={(platform, index, dragHandleProps) => {
                        const {
                            iconColor,
                            backgroundColor,
                            title,
                        } = STREAMING_PLATFORMS_INFO[platform.name] || {};

                        return (
                            <div
                                className="streamingPlatformsContentPlatformLinkContainer"
                                style={styx(
                                    sty.streamingPlatformsContentPlatformLinkContainer,
                                    {
                                        backgroundColor,
                                        opacity: platform.isEnabled ? 1 : DISABLED_OPACITY,
                                    }
                                )}
                            >
                                <div
                                    {...dragHandleProps}
                                    className="streamingPlatformsContentPlatformLinkDragHandle"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'grab',
                                    }}
                                >
                                    <Icon
                                        className="streamingPlatformsContentPlatformLinkDragHandleIcon"
                                        icon="drag"
                                        size={24}
                                        color={Colors.grey}
                                    />
                                </div>
                                <PlatformLink
                                    className="streamingPlatformsContentPlatformLink"
                                    title={title}
                                    description={platform.description}
                                    icon={iconColor || ''}
                                    link={platform.link}
                                    isEnabled={platform.isEnabled}
                                    placeholder={platform.placeholder}
                                    isLabel={false}
                                    onBlur={(newLink: string) => {
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].streamingPlatformsContent.platforms[index].link = httpsLink(newLink);
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                    onRemove={() => {
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].streamingPlatformsContent.platforms.splice(index, 1);
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                    onChange={(newLink: string) => {
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].streamingPlatformsContent.platforms[index].link = newLink;
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                    onToggleEnabled={() => {
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].streamingPlatformsContent.platforms[index].isEnabled = !platform.isEnabled;
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                />
                            </div>
                        )
                    }}
                />
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    streamingPlatformsContentComponent: {
        display: 'flex',
        flexDirection: 'column',
    },
    streamingPlatformsContentAddPlatformModalPlatform: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        gap: 8,
        padding: 16,
        borderRadius: 16,
    },
    streamingPlatformsContentAddPlatformModalPlatformDisabled: {
        cursor: 'not-allowed',
        opacity: 0.5,
    },
    alreadyAddedText: {
        marginLeft: 'auto',
        fontSize: 14,
    },
    streamingPlatformsContentPlatformLinkContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: 8,
        borderRadius: 16,
    },

    streamingPlatformsContentPlatformsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
    },
    streamingPlatformsContentPlatform: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        borderRadius: 16,
        backgroundColor: Colors.greyLight,
        cursor: 'pointer',
    },
}

export default StreamingPlatformsContent;